import { classNames } from '@chiroup/core/functions/classNames';
import { RadioGroup } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import ReactDOMServer from 'react-dom/server';
import React from 'react';
import { Tooltip } from 'react-tooltip';

type Props = {
  providers: any[];
  onChange: (provider: string) => void;
  value: string | null;
};

const ScheduleProviders: React.FC<Props> = ({ providers, onChange, value }) => {
  const providerOptions = providers?.concat({
    ID: 'no-preference',
    name: 'No preference? See all provider availability.',
  });

  return (
    <RadioGroup
      value={value}
      onChange={onChange}
      id="providers"
      className="mt-12"
    >
      <RadioGroup.Label>
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-900 whitespace-nowrap">
            Select a provider
          </span>
          <div className="w-full border-t border-gray-300 flex-grow ml-2" />
        </div>
      </RadioGroup.Label>
      <div className="mt-4 grid grid-cols-1 gap-y-2 sm:gap-y-6 sm:grid-cols-2 sm:gap-x-4">
        {providerOptions?.map((provider, idx) => (
          <RadioGroup.Option
            key={provider.ID || idx}
            value={provider.ID}
            className={({ checked, active }) =>
              classNames(
                provider.ID !== 'no-preference' && 'shadow-sm',
                checked || provider.ID === 'no-preference'
                  ? 'border-transparent'
                  : 'border-gray-300',
                active ? 'border-primary ring-2 ring-primary' : '',
                'relative flex cursor-pointer rounded-lg border bg-white p-4 focus:outline-none',
                provider.ID === 'no-preference' ? 'sm:col-span-2' : '',
              )
            }
          >
            {({ checked, active }) => (
              <>
                <span className="flex flex-1">
                  {provider.ID === 'no-preference' ? (
                    <RadioGroup.Label
                      as="span"
                      className="text-sm font-semibold leading-6 text-gray-900 text-center w-full"
                    >
                      <span className="absolute inset-x-0 -top-px bottom-0" />
                      {provider.name}
                    </RadioGroup.Label>
                  ) : (
                    <div className="flex gap-x-4">
                      {provider.profileImage ? (
                        <img
                          className="h-12 w-12 flex-none rounded-full bg-gray-50"
                          src={provider.profileImage}
                          alt={provider.name}
                        />
                      ) : (
                        <span className="inline-block h-12 w-12 overflow-hidden rounded-full bg-gray-100 mx-auto">
                          <svg
                            className="h-full w-full text-gray-300"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </span>
                      )}
                      <div className="min-w-0 flex-auto text-left">
                        <RadioGroup.Label
                          as="span"
                          className="text-sm font-semibold leading-6 text-gray-900"
                        >
                          <div className="flex flex-row gap-2 items-center">
                            <span>{provider.name}</span>
                            {!!provider.bio && (
                              <>
                                <InformationCircleIcon
                                  data-tooltip-id={`tt-bio-${provider.ID}`}
                                  data-tooltip-html={ReactDOMServer.renderToStaticMarkup(
                                    <div className="w-64 sm:w-96 flex flex-col gap-4">
                                      {provider.bio
                                        ?.split(/\n+/g)
                                        ?.map((line: string, idx2: number) => (
                                          <div key={idx2}>{line}</div>
                                        ))}
                                    </div>,
                                  )}
                                  className="inline-block h-4 w-4 text-gray-400 cursor-pointer z-50 hover:text-gray-600"
                                />
                                <Tooltip id={`tt-bio-${provider.ID}`} />
                              </>
                            )}
                          </div>
                        </RadioGroup.Label>
                        <RadioGroup.Description
                          as="span"
                          className="mt-1 flex text-xs leading-5 text-gray-500"
                        >
                          <span className="relative truncate">
                            {provider.degree}
                          </span>
                        </RadioGroup.Description>
                      </div>
                    </div>
                  )}
                </span>
                <CheckCircleIcon
                  className={classNames(
                    !checked ? 'invisible' : '',
                    'h-5 w-5 text-primary',
                  )}
                  aria-hidden="true"
                />
                <span
                  className={classNames(
                    active ? 'border' : 'border-2',
                    checked ? 'border-primary' : 'border-transparent',
                    'pointer-events-none absolute -inset-px rounded-lg',
                  )}
                  aria-hidden="true"
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );
};

export default ScheduleProviders;
